import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import {
  PAYWALL_CONTENT_MAX_WIDTH_CLASS,
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_LINKS,
  PAYWALL_PLAN_FEATURES,
  PAYWALL_SUPPORT_EMAIL,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallPdfPage,
  PaywallPriceText,
} from './components';
import { CircleCheckIcon, DocumentReadyIcon } from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { splitFileName } from './utils';

const PriceText = ({ value }: { value: string }) => (
  <PaywallPriceText value={value} className="text-[#292A33]" />
);

const ExtensionBadge = ({
  extension,
  className = '',
}: {
  extension: string;
  className?: string;
}) => (
  <span
    className={clsx(
      'inline-flex items-center justify-center rounded-[4px] border-2 border-white p-2 text-[14px] font-medium uppercase leading-[20px] text-white',
      PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400',
      className
    )}
  >
    {extension}
  </span>
);

const PdfPreview = ({
  file,
  width,
  height,
  className = '',
}: {
  file: File;
  width: number;
  height: number;
  className?: string;
}) => (
  <div className={clsx('bg-white', className)} style={{ width }}>
    <PaywallPdfPage
      file={file}
      width={width}
      fallback={<span className="block bg-white" style={{ width, height }} />}
    />
  </div>
);

const DocumentReadyMessage = ({
  extension,
  showBadge,
  className = '',
}: {
  extension: string;
  showBadge: boolean;
  className?: string;
}) => (
  <div
    className={clsx(
      'flex w-full items-center justify-between rounded-[16px] bg-[#DAF6ED]',
      className
    )}
  >
    <span className="flex items-center gap-2.5">
      <DocumentReadyIcon className="h-6 w-6 shrink-0" aria-hidden />
      <span className="text-[16px] leading-5 text-[#292A33]">
        Your document is ready!
      </span>
    </span>
    {showBadge && <ExtensionBadge extension={extension} />}
  </div>
);

const Paywall2Document = ({
  documentName,
  documentExtension,
  previewFile,
}: {
  documentName: string;
  documentExtension: string;
  previewFile: File | null;
}) => {
  const { base, suffix } = splitFileName(documentName, documentExtension);

  return (
    <div
      className={clsx(
        'w-full rounded-[20px] border border-[#D9D9D9] bg-white p-1 lg:p-2',
        previewFile && 'pb-4 lg:pb-2'
      )}
    >
      <DocumentReadyMessage
        extension={documentExtension}
        showBadge={false}
        className="px-4 py-2 lg:hidden"
      />
      <DocumentReadyMessage
        extension={documentExtension}
        showBadge
        className="hidden px-4 py-6 lg:flex"
      />

      {previewFile && (
        <>
          <div className="flex items-center justify-center gap-4 px-8 pt-4 lg:hidden">
            <div className="relative shrink-0">
              <PdfPreview
                file={previewFile}
                width={96}
                height={136}
                className="rounded-[4px] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.16)]"
              />
              <ExtensionBadge
                extension={documentExtension}
                className="pointer-events-none absolute -left-4 top-4"
              />
            </div>
            <p
              title={documentName}
              className="flex min-w-0 text-[14px] text-[#787887]"
            >
              <span className="-mr-px min-w-0 truncate">{base}</span>
              <span className="shrink-0">{suffix}</span>
            </p>
          </div>

          <div className="mt-2 hidden max-h-[570px] w-full items-start justify-center overflow-hidden rounded-[16px] bg-[#EEEEF2] lg:flex">
            <PdfPreview
              file={previewFile}
              width={380}
              height={537}
              className="mb-9 mt-9 rounded-[16px]"
            />
          </div>
        </>
      )}
    </div>
  );
};

const TotalBanner = ({ amountText }: { amountText: string }) => (
  <div className="flex w-full items-center justify-between overflow-hidden rounded-[20px] bg-[#DBE7FF] px-4 py-6 lg:p-6">
    <span className="text-[16px] font-medium text-[#292A33] lg:text-[25px]">
      Total due today:
    </span>
    <PriceText value={amountText} />
  </div>
);

const FullAccessSection = ({
  accessTitle,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  showTopDivider,
  className = '',
}: {
  accessTitle: string;
  hasTrial: boolean;
  trialDurationText: string;
  dueTodayAmountText: string;
  recurringAmountText: string;
  recurringPeriodText: string;
  showTopDivider: boolean;
  className?: string;
}) => (
  <div className={clsx('w-full flex-col gap-4 lg:gap-6', className)}>
    {showTopDivider && <span className="h-px w-full bg-[#EBEBEB]" />}
    <h3 className="text-[18px] font-bold uppercase text-[#292A33]">
      {accessTitle}
    </h3>
    <ul className="flex w-full flex-col gap-2.5">
      {PAYWALL_PLAN_FEATURES.map((feature) => (
        <li className="flex w-full items-center gap-2" key={feature}>
          <CircleCheckIcon
            className="h-6 w-6 shrink-0 text-[#016FD0]"
            aria-hidden
          />
          <span className="text-[16px] font-medium text-[#292A33] lg:text-[18px]">
            {feature}
          </span>
        </li>
      ))}
    </ul>
    <span className="h-px w-full bg-[#EBEBEB]" />
    <p className="text-[14px] leading-5 text-[#787887]">
      By continuing, you agree that you will be charged{' '}
      {hasTrial && trialDurationText ? (
        <>
          {dueTodayAmountText} today for a {trialDurationText} trial and{' '}
          {recurringAmountText} automatically every {recurringPeriodText}{' '}
          thereafter unless you cancel before your trial ends.
        </>
      ) : (
        <>
          {recurringAmountText} automatically every {recurringPeriodText}{' '}
          unless you cancel before your next renewal.
        </>
      )}{' '}
      You can cancel your auto-renewing subscription through your online
      account. You also agree to our{' '}
      <Link
        href={PAYWALL_LINKS.terms}
        target="_blank"
        className="text-[#4B7EFF] underline"
      >
        Terms and Conditions
      </Link>
      ,{' '}
      <Link
        href={PAYWALL_LINKS.privacy}
        target="_blank"
        className="text-[#4B7EFF] underline"
      >
        Privacy Policy
      </Link>
      ,{' '}
      <Link
        href={PAYWALL_LINKS.subscription}
        target="_blank"
        className="text-[#4B7EFF] underline"
      >
        Subscription Policy
      </Link>
      , and{' '}
      <Link
        href={PAYWALL_LINKS.refund}
        target="_blank"
        className="text-[#4B7EFF] underline"
      >
        Refund Policy
      </Link>
      . For customer support, please email us at{' '}
      <a
        href={`mailto:${PAYWALL_SUPPORT_EMAIL}`}
        className="text-[#4B7EFF] underline"
      >
        {PAYWALL_SUPPORT_EMAIL}
      </a>
      .
    </p>
  </div>
);

const Paywall2Layout = ({
  isOnlyPayment,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  documentName,
  documentExtension,
  documentFile,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    isOnlyPayment: boolean;
    hasTrial: boolean;
    trialDurationText: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    recurringPeriodText: string;
  }) => {
  const previewFile =
    !isOnlyPayment && documentFile?.type === 'application/pdf'
      ? documentFile
      : null;
  const accessTitle =
    hasTrial && trialDurationText
      ? `${trialDurationText} full access`
      : 'Full access';
  const accessProps = {
    accessTitle,
    hasTrial,
    trialDurationText,
    dueTodayAmountText,
    recurringAmountText,
    recurringPeriodText,
  };

  return (
    <div className="min-h-full bg-[#F6F7F8] font-barlow text-[#292A33]">
      <header
        className={`mx-auto flex w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} items-center px-8 py-5`}
      >
        <img
          src="/images/logo/brand.svg"
          alt="PDF Genius"
          className="h-8 w-[136px]"
        />
      </header>

      <div
        className={`mx-auto flex w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} flex-col items-start gap-4 px-4 pb-6 lg:flex-row lg:px-8 lg:pb-8`}
      >
        <section className="order-2 w-full rounded-[12px] bg-white p-4 lg:order-1 lg:w-[54.81%] lg:px-8 lg:py-6">
          <div className="flex w-full items-center justify-between whitespace-nowrap">
            <span className="text-[20px] font-semibold text-[#292A33]">
              Total due today:
            </span>
            <PriceText value={dueTodayAmountText} />
          </div>

          {checkoutMethod && (
            <div className="relative mt-4 lg:mt-6">
              <PaywallCheckoutFrame
                checkoutContainerRef={checkoutContainerRef}
                paymentStatus={paymentStatus}
              />
            </div>
          )}

          <FullAccessSection
            {...accessProps}
            showTopDivider
            className={clsx('flex pt-4 lg:pt-6', !previewFile && 'lg:hidden')}
          />
        </section>

        <aside className="order-1 flex w-full flex-col gap-4 lg:order-2 lg:w-[45.19%]">
          {!isOnlyPayment && (
            <Paywall2Document
              documentName={documentName}
              documentExtension={documentExtension}
              previewFile={previewFile}
            />
          )}
          <TotalBanner amountText={dueTodayAmountText} />
          {!previewFile && (
            <FullAccessSection
              {...accessProps}
              showTopDivider={false}
              className="hidden rounded-[12px] bg-white p-4 lg:flex lg:px-8 lg:py-6"
            />
          )}
        </aside>
      </div>
    </div>
  );
};

export default Paywall2Layout;
